import { useEffect, useMemo, useState } from 'react';
import type { Connection, FieldSpec, ProviderId, ProviderInfo, TestResult } from '../types';
import { api, ApiError } from '../lib/api';

interface Props {
  providers: ProviderInfo[];
  /** null = creating */
  connection: Connection | null;
  onClose: () => void;
  onSaved: (connection: Connection, created: boolean) => void;
  onConnectOAuth: (provider: ProviderInfo) => void;
}

function isAdvanced(field: FieldSpec): boolean {
  return /^state|^status|^transition|ColumnId$|^wiql$|^jql$|^max|^apiVersion$|^baseUrl$|Field$|^update|^authHeader|^itemsPath$/.test(
    field.key
  );
}

export function ConnectionForm({ providers, connection, onClose, onSaved, onConnectOAuth }: Props) {
  const editing = Boolean(connection);
  const [providerId, setProviderId] = useState<ProviderId>(connection?.provider ?? 'azure_devops');
  const [name, setName] = useState(connection?.name ?? '');
  const [config, setConfig] = useState<Record<string, string>>(connection?.config ?? {});
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<TestResult | null>(null);

  const provider = useMemo(
    () => providers.find((p) => p.id === providerId) ?? providers[0],
    [providers, providerId]
  );

  useEffect(() => {
    if (!editing && provider && !name) setName(provider.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id]);

  /**
   * Switching system type should carry the name along, but only while the user
   * has not typed one of their own.
   */
  function selectProvider(next: ProviderInfo) {
    const untouched = !name.trim() || providers.some((p) => p.label === name.trim());
    setProviderId(next.id);
    if (untouched) setName(next.label);
    setConfig({});
    setCredentials({});
    setTest(null);
    setError(null);
  }

  if (!provider) return null;

  // Connecting one of these for the first time happens by signing in, not by
  // pasting credentials — there's nothing to fill in until that comes back,
  // so the whole config/credentials section is meaningless pre-connect.
  const isOAuthCreate = !editing && provider.authMode === 'oauth';

  const basicConfig = provider.configFields.filter((f) => !isAdvanced(f));
  const advancedConfig = provider.configFields.filter(isAdvanced);

  const payload = () => ({
    name: name.trim() || provider.label,
    provider: provider.id,
    config,
    credentials,
  });

  async function runTest() {
    setBusy('test');
    setError(null);
    setTest(null);
    try {
      // When editing without re-entering secrets, test the saved connection.
      const result =
        editing && !Object.keys(credentials).length
          ? await api.testConnection(connection!.id)
          : await api.testDraft(payload());
      setTest(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy('save');
    setError(null);
    try {
      const saved = editing
        ? await api.updateConnection(connection!.id, payload())
        : await api.createConnection(payload());
      onSaved(saved, !editing);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const renderField = (field: FieldSpec, values: Record<string, string>, onChange: (k: string, v: string) => void) => (
    <div className="form-row" key={field.key}>
      <label htmlFor={`f-${field.key}`}>
        {field.label}
        {field.required && <span aria-hidden> *</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          id={`f-${field.key}`}
          value={values[field.key] ?? ''}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      ) : (
        <input
          id={`f-${field.key}`}
          type={field.type === 'password' ? 'password' : 'text'}
          autoComplete={field.type === 'password' ? 'new-password' : 'off'}
          value={values[field.key] ?? ''}
          placeholder={
            field.type === 'password' && editing
              ? (connection?.credentialPreview?.[field.key] ?? 'leave blank to keep the saved value')
              : field.placeholder
          }
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      )}
      {field.help && <span className="help">{field.help}</span>}
    </div>
  );

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal wide" role="dialog" aria-modal="true" aria-label={editing ? 'Edit connection' : 'Add system'}>
        <div className="modal-head">
          <h3>{editing ? `Edit “${connection!.name}”` : 'Connect a system'}</h3>
        </div>
        <form onSubmit={save}>
          <div className="modal-body form-grid">
            {!editing && (
              <div className="form-row">
                <label>System type</label>
                <div className="provider-picker">
                  {providers.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="provider-option"
                      aria-pressed={p.id === providerId}
                      onClick={() => selectProvider(p)}
                    >
                      <div className="t">{p.label}</div>
                      <div className="d">{p.blurb}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <div className="notice error">{error}</div>}
            {test && <div className={`notice ${test.ok ? 'ok' : 'error'}`}>{test.message}</div>}

            {isOAuthCreate ? (
              <div className="notice info">
                {provider.label} connects by signing in — click “Connect {provider.label}” below, approve access in
                the pop-up, and it'll show up here as a real connection.
              </div>
            ) : (
              <>
                <div className="form-row">
                  <label htmlFor="conn-name">Connection name</label>
                  <input id="conn-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                  <span className="help">
                    Shown in the System column. Use something like “Contoso ADO” if you have several.
                  </span>
                </div>

                {basicConfig.map((f) => renderField(f, config, (k, v) => setConfig((c) => ({ ...c, [k]: v }))))}

                {provider.credentialFields.map((f) =>
                  renderField(f, credentials, (k, v) => setCredentials((c) => ({ ...c, [k]: v })))
                )}

                {provider.credentialFields.length > 0 && (
                  <div className="notice info">
                    Credentials are encrypted with AES-256-GCM before they are written to the database and are never
                    sent back to this page.
                  </div>
                )}

                {advancedConfig.length > 0 && (
                  <details className="advanced">
                    <summary>Advanced — field and status mapping</summary>
                    <div className="form-grid">
                      <span className="help">
                        Leave these blank to auto-detect. Set them when your project or board uses non-standard
                        column or state names.
                      </span>
                      {advancedConfig.map((f) =>
                        renderField(f, config, (k, v) => setConfig((c) => ({ ...c, [k]: v }))))}
                    </div>
                  </details>
                )}
              </>
            )}

            {provider.docsUrl && (
              <span className="help">
                API reference:{' '}
                <a href={provider.docsUrl} target="_blank" rel="noreferrer">
                  {provider.docsUrl}
                </a>
              </span>
            )}
          </div>

          <div className="modal-foot">
            {!isOAuthCreate && (
              <button type="button" className="btn" onClick={runTest} disabled={busy !== null}>
                {busy === 'test' && <span className="spin" />}
                Test connection
              </button>
            )}
            <div className="spacer" />
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy !== null}>
              Cancel
            </button>
            {isOAuthCreate ? (
              <button
                type="button"
                className="btn primary"
                disabled={busy !== null}
                onClick={() => {
                  onConnectOAuth(provider);
                  onClose();
                }}
              >
                Connect {provider.label}
              </button>
            ) : (
              <button type="submit" className="btn primary" disabled={busy !== null}>
                {busy === 'save' && <span className="spin" />}
                {editing ? 'Save changes' : 'Add system'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
